import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useExploracaoOperacional, type ExploracaoFilters } from '@/hooks/useExploracaoOperacional';
import { PageHeader } from '@/components/system/PageHeader';
import { SectionCard } from '@/components/system/SectionCard';
import { FilterBar } from '@/components/system/FilterBar';
import { DataTable, type Column } from '@/components/system/DataTable';
import { EmptyState } from '@/components/system/EmptyState';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Activity, MapPin, GitBranch, TrendingUp } from 'lucide-react';

const ExploracaoOperacionalPage: React.FC = () => {
  const { role } = useAuth();

  const [selectedCluster, setSelectedCluster] = useState<string>('__all__');
  const [selectedContrato, setSelectedContrato] = useState<string>('__all__');
  const [selectedMunicipio, setSelectedMunicipio] = useState<string>('__all__');
  const [selectedFrente, setSelectedFrente] = useState<string>('__all__');
  const [periodoInicio, setPeriodoInicio] = useState<string>('');
  const [periodoFim, setPeriodoFim] = useState<string>('');

  const filters: ExploracaoFilters = {
    clusterIds: selectedCluster === '__all__' ? [] : [selectedCluster],
    contratoId: selectedContrato === '__all__' ? null : selectedContrato,
    municipioIbge: selectedMunicipio === '__all__' ? null : selectedMunicipio,
    frenteId: selectedFrente === '__all__' ? null : selectedFrente,
    periodoInicio: periodoInicio || null,
    periodoFim: periodoFim || null,
  };

  const {
    isLoading,
    contratos,
    frentes,
    clusters,
    municipiosDisponiveis,
    evolucaoTemporal,
    distribuicaoMunicipio,
    rankingFrente,
    hasData,
  } = useExploracaoOperacional(filters);

  if (!['admin', 'coadmin', 'admin_cluster'].includes(role || '')) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
        Acesso restrito a administradores.
      </div>
    );
  }

  const frenteColumns: Column<typeof rankingFrente[0]>[] = [
    { key: 'nome', label: 'Frente', render: r => <span className="font-medium truncate max-w-[200px] block">{r.nome}</span> },
    { key: 'total', label: 'Execuções', align: 'center', render: r => <span className="text-sm tabular-nums">{r.total}</span> },
    { key: 'volume', label: 'Volume', align: 'right', render: r => <span className="text-sm tabular-nums font-medium">{r.volume.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}</span> },
  ];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Exploração Operacional"
        subtitle="Análise de execuções por período, município e frente de trabalho"
      />

      <FilterBar>
        {clusters.length > 1 && (
          <Select value={selectedCluster} onValueChange={v => { setSelectedCluster(v); setSelectedContrato('__all__'); setSelectedFrente('__all__'); }}>
            <SelectTrigger className="w-[180px] h-8 text-sm">
              <SelectValue placeholder="Cluster" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos os clusters</SelectItem>
              {clusters.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}

        <Select value={selectedContrato} onValueChange={v => { setSelectedContrato(v); setSelectedFrente('__all__'); }}>
          <SelectTrigger className="w-[200px] h-8 text-sm">
            <SelectValue placeholder="Contrato" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos os contratos</SelectItem>
            {contratos.map(c => <SelectItem key={c.id} value={c.id}>{c.nome || c.id.slice(0, 8)}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={selectedMunicipio} onValueChange={setSelectedMunicipio}>
          <SelectTrigger className="w-[180px] h-8 text-sm">
            <SelectValue placeholder="Município" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos os municípios</SelectItem>
            {municipiosDisponiveis.map(m => <SelectItem key={m.ibge} value={m.ibge}>{m.nome}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={selectedFrente} onValueChange={setSelectedFrente}>
          <SelectTrigger className="w-[180px] h-8 text-sm">
            <SelectValue placeholder="Frente" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todas as frentes</SelectItem>
            {frentes.map(f => <SelectItem key={f.id} value={f.id}>{f.nome}</SelectItem>)}
          </SelectContent>
        </Select>

        <Input
          type="month"
          value={periodoInicio}
          onChange={e => setPeriodoInicio(e.target.value)}
          placeholder="Início"
          className="w-[140px] h-8 text-sm"
        />
        <Input
          type="month"
          value={periodoFim}
          onChange={e => setPeriodoFim(e.target.value)}
          placeholder="Fim"
          className="w-[140px] h-8 text-sm"
        />
      </FilterBar>

      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-64" />
          <Skeleton className="h-48" />
        </div>
      ) : !hasData ? (
        <EmptyState text="Nenhuma execução encontrada com os filtros selecionados." />
      ) : (
        <>
          {/* Evolução Temporal */}
          <SectionCard title="Evolução Temporal de Execuções" icon={TrendingUp}>
            <div style={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={evolucaoTemporal} margin={{ left: 0, right: 16, top: 4, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.4} />
                  <XAxis dataKey="periodo" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} axisLine={false} />
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', fontSize: 12 }}
                    formatter={(v: number, name: string) => [v.toLocaleString('pt-BR'), name === 'volume' ? 'Volume' : 'Execuções']}
                  />
                  <Line type="monotone" dataKey="volume" name="Volume" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                  <Line type="monotone" dataKey="total" name="Execuções" stroke="hsl(var(--success))" strokeWidth={1.5} dot={false} strokeDasharray="4 2" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </SectionCard>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Execuções por Município */}
            <SectionCard title="Execuções por Município" icon={MapPin}>
              <div style={{ height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={distribuicaoMunicipio} layout="vertical" margin={{ left: 80, right: 16, top: 4, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.4} />
                    <XAxis type="number" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} axisLine={false} />
                    <YAxis
                      type="category"
                      dataKey="nome"
                      tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                      tickLine={false}
                      axisLine={false}
                      width={75}
                    />
                    <Tooltip
                      contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', fontSize: 12 }}
                      formatter={(v: number) => [v.toLocaleString('pt-BR'), 'Volume']}
                    />
                    <Bar dataKey="volume" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </SectionCard>

            {/* Tabela por Frente */}
            <SectionCard title="Execuções por Frente" icon={GitBranch}>
              <DataTable
                columns={frenteColumns}
                data={rankingFrente}
                keyExtractor={r => r.frente_id}
                emptyText="Nenhuma frente encontrada."
              />
            </SectionCard>
          </div>
        </>
      )}
    </div>
  );
};

export default ExploracaoOperacionalPage;
